import React, { useState } from 'react';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import Logo from '../../images/logo.svg';
import './style.css';

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Validate email format
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            return t('forgotPassword.emailRequired');
        }
        if (!emailRegex.test(email)) {
            return t('forgotPassword.invalidEmail');
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage('');
        setErrorMessage('');

        // Validate email
        const emailErr = validateEmail(email);
        setEmailError(emailErr);

        if (emailErr) {
            return;
        }

        setIsSubmitting(true);

        try {
            await authService.forgotPassword(email);
            setSuccessMessage(t('forgotPassword.successMessage'));
            toast.success(t('forgotPassword.successToast'));

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            const errMsg = err.response?.data?.error?.message || t('forgotPassword.failedMessage');
            setErrorMessage(errMsg);
            toast.error(errMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (emailError) {
            setEmailError('');
        }
        if (errorMessage) {
            setErrorMessage('');
        }
    };

    return (
        <div className="wpo-account-area">
            {/* Navigation Header */}
            <div className="auth-page-header">
                <Link to="/" className="back-to-home">
                    <i className="ti-arrow-left"></i>
                </Link>
                <Link to="/" className="auth-logo">
                    <img src={Logo} alt="Anas Logo" />
                </Link>
            </div>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <form onSubmit={handleSubmit} className="wpo-account-form">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <div className="wpo-account-form-area">
                                        <div className="wpo-logo">
                                            <h2>{t('forgotPassword.title')}</h2>
                                            <p style={{
                                                fontSize: '14px',
                                                color: '#666',
                                                marginTop: '10px',
                                                marginBottom: '20px'
                                            }}>
                                                {t('forgotPassword.description')}
                                            </p>
                                        </div>

                                        {/* Success Alert */}
                                        {successMessage && (
                                            <Alert severity="success" style={{ marginBottom: '20px' }}>
                                                {successMessage}
                                            </Alert>
                                        )}

                                        {/* Error Alert */}
                                        {errorMessage && (
                                            <Alert
                                                severity="error"
                                                onClose={() => setErrorMessage('')}
                                                style={{ marginBottom: '20px' }}
                                            >
                                                {errorMessage}
                                            </Alert>
                                        )}

                                        <div className="wpo-account-form">
                                            <TextField
                                                fullWidth
                                                label={t('forgotPassword.emailLabel')}
                                                variant="outlined"
                                                type="email"
                                                value={email}
                                                onChange={handleEmailChange}
                                                error={!!emailError}
                                                helperText={emailError}
                                                disabled={isSubmitting || !!successMessage}
                                                margin="normal"
                                            />
                                        </div>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            className="theme-btn"
                                            disabled={isSubmitting || !!successMessage}
                                            style={{ marginTop: '10px' }}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
                                                    {t('forgotPassword.sending')}
                                                </>
                                            ) : (
                                                t('forgotPassword.sendResetLink')
                                            )}
                                        </Button>
                                        <div className="account-form-link" style={{ marginTop: '20px' }}>
                                            <p>{t('forgotPassword.rememberPassword')} <Link to="/login">{t('forgotPassword.backToLogin')}</Link></p>
                                            <p>{t('forgotPassword.noAccount')} <Link to="/signup">{t('signupNav')}</Link></p>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;